import { InputSwitch } from "primereact/inputswitch";
import { useState } from "react";
import { Bar } from "react-chartjs-2";
import PendingRejectTable from "./PendingRejectTable";
import { pendingBackgroundColor } from "../../../../util/chartColours";
import { formatGBP } from "../../leak detection/helper/helperFunctions";

const BarCharts = ({ dashBoardData }: any) => {
  const [showManagerBarChart, setShowManagerBarChart] =
    useState<boolean>(false);
  const [showSeniorManagerBarChart, setShowSeniorManagerBarChart] =
    useState<boolean>(false);
  const [showDirectorBarChart, setShowDirectorBarChart] =
    useState<boolean>(false);

  const [showManagerAnimation, setShowManagerAnimation] =
    useState<boolean>(false);
  const [showSeniorManagerAnimation, setShowSeniorManagerAnimation] =
    useState<boolean>(false);
  const [showDirectorAnimation, setShowDirectorAnimation] =
    useState<boolean>(false);

  const managerData = {
    labels: dashBoardData?.managerPendings.map(
      (manager: any) => manager.displayname
    ),
    datasets: [
      {
        label: "Pending Approval",
        data: dashBoardData?.managerPendings.map(
          (manager: any) => manager.pendingapprovel
        ),
        amountData: dashBoardData?.managerPendings.map(
          (manager: any) => manager.pendingAmount
        ),
        backgroundColor: pendingBackgroundColor,
      },
      {
        label: "Rejected Invoices",
        data: dashBoardData?.managerRejected.map(
          (manager: any) => manager.rejectedapprovel
        ),
        amountData: dashBoardData?.managerRejected.map(
          (manager: any) => manager.rejectedAmount
        ),
        backgroundColor: "#D84040",
      },
    ],
  };

  const seniorManagerData = {
    labels: dashBoardData?.seniorManagerPendings.map(
      (seniorManager: any) => seniorManager.displayname
    ),
    datasets: [
      {
        label: "Pending Approval",
        data: dashBoardData?.seniorManagerPendings.map(
          (seniorManager: any) => seniorManager.pendingapprovel
        ),
        amountData: dashBoardData?.seniorManagerPendings.map(
          (seniorManager: any) => seniorManager.pendingAmount
        ),
        backgroundColor: pendingBackgroundColor,
      },
      {
        label: "Rejected Invoices",
        data: dashBoardData?.seniorManagerRejected.map(
          (seniorManager: any) => seniorManager.rejectedapprovel
        ),
        amountData: dashBoardData?.seniorManagerRejected.map(
          (seniorManager: any) => seniorManager.rejectedAmount
        ),
        backgroundColor: "#D84040",
      },
    ],
  };

  const directorData = {
    labels: dashBoardData?.directorPendings.map(
      (director: any) => director.displayname
    ),
    datasets: [
      {
        label: "Pending Approvals",
        data: dashBoardData?.directorPendings.map(
          (director: any) => director.pendingapprovel
        ),
        amountData: dashBoardData?.directorPendings.map(
          (director: any) => director.pendingAmount
        ),
        backgroundColor: pendingBackgroundColor,
      },
      {
        label: "Rejected Invoices",
        data: dashBoardData?.directorRejected.map(
          (director: any) => director.rejectedapprovel
        ),
        amountData: dashBoardData?.directorRejected.map(
          (director: any) => director.rejectedAmount
        ),
        backgroundColor: "#D84040",
      },
    ],
  };

  const handleShowAmountInGBP = (rowData: any, type: string = "") => {
    return rowData
      .split(",")
      .map(
        (joinedAmount: any, index: number) =>
          `${
            index === 0 ? joinedAmount.slice(0, 1) : joinedAmount.slice(1, 2)
          } ${formatGBP(joinedAmount.trim().slice(1))}`
      )
      .join(", ");
  };

  const formatAmount = (amount: string) => {
    const trimmedAmount = amount.trim();

    if (trimmedAmount === ",") {
      return "0";
    }

    // If the amount has a valid currency format (like "€ 258 , £ 822"), return as is.
    if (trimmedAmount.match(/^[€£$]\s*\d+(?:\s*,\s*[€£$]\s*\d+)*$/)) {
      return trimmedAmount;
    }

    // Handle cases where amount starts or ends with ","
    return handleShowAmountInGBP(trimmedAmount.replace(/^,\s*|\s*,\s*$/g, ""));
  };

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem: any) {
            return `${tooltipItem.dataset.label} ${"          "}Amount: ${
              formatAmount(
                tooltipItem.dataset.amountData[tooltipItem.dataIndex]
              ) || 0
            }, Count: ${tooltipItem.raw || 0}`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  return (
    <div className="tw-grid tw-grid-cols-12 tw-gap-4">
      <div className="tw-col-span-12">
        {/* Bar Charts Section */}
        {dashBoardData?.managerPendings?.length !== 0 && (
          <div className="tw-mt-[10px]">
            <div className="tw-flex tw-justify-between tw-items-center">
              <h3 className="tw-text-xl tw-font-semibold tw-mb-4">
                Manager - wise pending and rejected invoices data:-
              </h3>
              <div className="tw-flex tw-items-center">
                <h3 className="tw-mr-1 tw-font-semibold">Table</h3>
                <InputSwitch
                  checked={showManagerBarChart}
                  onChange={(e) => {
                    setShowManagerAnimation(true);
                    setShowManagerBarChart(e.value);
                  }}
                />
                <h3 className="tw-ml-1 tw-font-semibold">Chart</h3>
              </div>
            </div>
            {showManagerBarChart ? (
              <Bar
                data={managerData}
                options={options}
                className="flipLeftToRight"
                height={"100px"}
              />
            ) : (
              <PendingRejectTable
                dashBoardData={dashBoardData}
                userPendings="managerPendings"
                userRejected="managerRejected"
                showAnimation={showManagerAnimation}
              />
            )}
            <hr className="tw-my-4 tw-w-full tw-border-2 tw-border-gray-500 tw-h-[2px] tw-mb-[-15px] tw-mt-[40px]" />
          </div>
        )}
      </div>
      <div className="tw-col-span-12">
        {/* Bar Charts Section */}
        {dashBoardData?.seniorManagerPendings?.length !== 0 && (
          <div className="tw-mt-[10px]">
            <div className="tw-flex tw-justify-between tw-items-center">
              <h3 className="tw-text-xl tw-font-semibold tw-mb-4">
                Senior Manager - wise pending and rejected invoices data:-
              </h3>
              <div className="tw-flex tw-items-center">
                <h3 className="tw-mr-1 tw-font-semibold">Table</h3>
                <InputSwitch
                  checked={showSeniorManagerBarChart}
                  onChange={(e) => {
                    setShowSeniorManagerAnimation(true);
                    setShowSeniorManagerBarChart(e.value);
                  }}
                />
                <h3 className="tw-ml-1 tw-font-semibold">Chart</h3>
              </div>
            </div>
            {showSeniorManagerBarChart ? (
              <Bar
                data={seniorManagerData}
                options={options}
                className="flipLeftToRight"
                height={"100px"}
              />
            ) : (
              <PendingRejectTable
                dashBoardData={dashBoardData}
                userPendings="seniorManagerPendings"
                userRejected="seniorManagerRejected"
                showAnimation={showSeniorManagerAnimation}
              />
            )}
            <hr className="tw-my-4 tw-w-full tw-border-2 tw-border-gray-500 tw-h-[2px] tw-mb-[-15px] tw-mt-[40px]" />
          </div>
        )}
      </div>
      <div className="tw-col-span-12">
        {dashBoardData?.directorPendings?.length !== 0 && (
          <div className="tw-mt-8">
            <div className="tw-flex tw-justify-between tw-items-center">
              <h3 className="tw-text-xl tw-font-semibold">
                Director - wise pending and rejected invoices data:-
              </h3>
              <div className="tw-flex tw-items-center">
                <h3 className="tw-mr-1 tw-font-semibold">Table</h3>
                <InputSwitch
                  checked={showDirectorBarChart}
                  onChange={(e) => {
                    setShowDirectorAnimation(true);
                    setShowDirectorBarChart(e.value);
                  }}
                />
                <h3 className="tw-ml-1 tw-font-semibold">Chart</h3>
              </div>
            </div>
            {showDirectorBarChart ? (
              <Bar
                data={directorData}
                options={options}
                className="flipLeftToRight"
                height={"100px"}
              />
            ) : (
              <PendingRejectTable
                dashBoardData={dashBoardData}
                userPendings="directorPendings"
                userRejected="directorRejected"
                showAnimation={showDirectorAnimation}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BarCharts;
